/* ═══════════════════════════════════════════════════════════════
   Live Sync — WebSocket client for real-time data updates
   Connects to EC2 WebSocket server, applies incoming changes
   to the local cloud cache so all clients stay in sync.
   ═══════════════════════════════════════════════════════════════ */

import { getAuthToken } from './authService';
import { applyRealtimeEvent } from './cloudCache';
import { API_ORIGIN } from '../lib/apiBase';

// Resolve the realtime WebSocket origin from the SAME config the HTTP API uses,
// so behavior is unchanged on desktop and correct on web:
//  - Electron / fallback: API_ORIGIN='https://18.223.55.17' -> wss://18.223.55.17/ws (identical to before)
//  - Web build (same-origin): API_ORIGIN='' -> wss://<this host>/ws, proxied by the Cloudflare Pages worker
//  - Explicit origin: API_ORIGIN='https://api.specforma.app' -> wss://api.specforma.app/ws
/** The realtime socket URL for a server path (`/ws` live sync, `/collab`
 *  shared drafts) — same origin rules for both, so the collab socket can never
 *  drift to a different host than the live-sync one. */
export function resolveSocketUrl(path: string): string {
  if (!API_ORIGIN) {
    if (typeof window !== 'undefined' && window.location) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}${path}`;
    }
    return `wss://18.223.55.17${path}`;
  }
  return `${API_ORIGIN.replace(/^http/, 'ws')}${path}`;
}

const WS_URL = resolveSocketUrl('/ws');

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;

export function connectLiveSync(): void {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  const token = getAuthToken();
  if (!token) return; // Not logged in yet

  intentionalClose = false;

  try {
    ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
  } catch {
    scheduleReconnect();
    return;
  }

  ws.onopen = () => {
    console.log('[LiveSync] Connected');
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'connected') return;

      if (msg.type === 'presence:snapshot') {
        handlePresenceSnapshot(msg.presence || []);
      } else if (msg.type === 'save' && msg.table && msg.record) {
        applyRealtimeEvent(msg.table, 'save', msg.record);
      } else if (msg.type === 'remove' && msg.table && msg.id) {
        applyRealtimeEvent(msg.table, 'remove', { id: msg.id });
      } else if (msg.type === 'update' && msg.table && msg.records) {
        applyRealtimeEvent(msg.table, 'update', msg.records);
      } else if (msg.type === 'orgchart:drag') {
        orgChartDragListeners.forEach((cb) => cb(msg));
      } else if (msg.type === 'orgchart:cursor') {
        orgChartCursorListeners.forEach((cb) => cb(msg));
      }
    } catch {}
  };

  ws.onclose = () => {
    ws = null;
    if (!intentionalClose) scheduleReconnect();
  };

  ws.onerror = () => {};
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectLiveSync();
  }, 3000);
}

export function disconnectLiveSync(): void {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    ws.close();
    ws = null;
  }
}

// ─── Presence: who's editing which spec/variant ───

export interface PresenceEntry {
  specId: string;
  variantId: string | null;
  userName: string;
}

let presenceListeners: Array<(presence: PresenceEntry[]) => void> = [];

export function onPresenceUpdate(cb: (presence: PresenceEntry[]) => void): () => void {
  presenceListeners.push(cb);
  return () => {
    presenceListeners = presenceListeners.filter((l) => l !== cb);
  };
}

export function handlePresenceSnapshot(presence: PresenceEntry[]): void {
  presenceListeners.forEach((cb) => cb(presence));
}

export function sendPresenceJoin(specId: string, variantId: string | null, userName: string): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'presence:join', specId, variantId, userName }));
  }
}

export function sendPresenceLeave(): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'presence:leave' }));
  }
}

// ─── Org Chart: live multiplayer drag + cursor presence ───
//
// Stateless on the server — every client tracks its own TTL for remote
// drag + cursor entries (drop after ~3s of silence). Senders throttle
// to ~33ms (30 fps) so a fast drag doesn't flood the socket. The save
// of the final position is handled by the existing table-save broadcast
// path (cloudCache.save → POST → WS save event), so onNodeDragStop
// doesn't have to send a duplicate orgchart:drag message.

export interface OrgChartDragMessage {
  type: 'orgchart:drag';
  userId: string;
  userName: string;
  nodes: Array<{ id: string; x: number; y: number; type: 'person' | 'junction' }>;
  active: boolean;
}

export interface OrgChartCursorMessage {
  type: 'orgchart:cursor';
  userId: string;
  userName: string;
  x: number;
  y: number;
  leave: boolean;
}

let orgChartDragListeners: Array<(msg: OrgChartDragMessage) => void> = [];
let orgChartCursorListeners: Array<(msg: OrgChartCursorMessage) => void> = [];

export function onOrgChartDrag(cb: (msg: OrgChartDragMessage) => void): () => void {
  orgChartDragListeners.push(cb);
  return () => {
    orgChartDragListeners = orgChartDragListeners.filter((l) => l !== cb);
  };
}

export function onOrgChartCursor(cb: (msg: OrgChartCursorMessage) => void): () => void {
  orgChartCursorListeners.push(cb);
  return () => {
    orgChartCursorListeners = orgChartCursorListeners.filter((l) => l !== cb);
  };
}

export function sendOrgChartDrag(
  nodes: Array<{ id: string; x: number; y: number; type: 'person' | 'junction' }>,
  active: boolean
): void {
  if (ws && ws.readyState === WebSocket.OPEN && nodes.length > 0) {
    ws.send(JSON.stringify({ type: 'orgchart:drag', nodes, active }));
  }
}

export function sendOrgChartCursor(x: number, y: number): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'orgchart:cursor', x, y, leave: false }));
  }
}

export function sendOrgChartCursorLeave(): void {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'orgchart:cursor', x: 0, y: 0, leave: true }));
  }
}
